import { type ReactNode } from "react";
import { useAgentStore } from "@/stores/agent-store";
import { Button } from "@/components/design/Button";
import type { ButtonSize, ButtonVariant } from "@/components/design/button-classes";
import { SmikeGlyph } from "./SmikeGlyph";

// The one affordance for a seeded assistant hand-off — reuse it everywhere
// instead of hand-rolling another smike + Button.
//
// The smike mascot is rendered through `currentColor` (a mask over bg-current),
// so it inverts WITH the button rather than vanishing: dark at rest, white on
// the secondary button's black hover fill. smike.svg itself is a fixed-black
// asset, so a plain <img> would disappear on the inverted state.
export function AskAssistantButton({
  prompt,
  hiddenContext,
  label,
  disabled,
  variant = "secondary",
  size = "compact",
  className = "",
}: {
  /** The seeded prompt, or a factory called on click (e.g. to vary the phrasing each time). */
  prompt: string | (() => string);
  /**
   * Payload the model needs but the user should not read in their own bubble — a catalogue, a brief.
   * Sent appended to `prompt`, hidden at render (see AGENT_CONTEXT_MARKER).
   */
  hiddenContext?: string | (() => string);
  label: ReactNode;
  disabled?: boolean;
  variant?: ButtonVariant;
  size?: ButtonSize;
  className?: string;
}) {
  const ask = useAgentStore((s) => s.ask);
  const open = useAgentStore((s) => s.open);
  return (
    <Button
      variant={variant}
      size={size}
      disabled={disabled}
      onClick={() => {
        const context = typeof hiddenContext === "function" ? hiddenContext() : hiddenContext;
        ask(typeof prompt === "function" ? prompt() : prompt, context);
      }}
      className={`gap-1.5 ${className}`}
    >
      {/* Turn the mascot's head to face the chat while the assistant panel is open. */}
      {/* `-my-1` keeps the 20px mascot from driving the control height: the size scale in
          button-classes is padding-based around a ~16px text line box, so an unclipped 20px glyph
          made every AskAssistantButton 4px taller than the plain Button beside it. The negative
          margin trims its margin box back under the line box; the mascot still renders at 20px. */}
      <SmikeGlyph
        className={`-my-1 -ml-0.5 h-5 w-5 transition-transform duration-200 motion-reduce:transition-none ${
          open ? "-scale-x-100" : ""
        }`}
      />
      {label}
    </Button>
  );
}
